import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';

import { AuthService } from '../../auth/auth.service';
import { CompanyType } from '../../brand/company-type.enum';
import { IndustrySector } from '../../brand/industry-sector.enum';
import { companyTypeValidator } from '../../form/company-type.validator';
import { FormHelper } from '../../form/form-helper';
import { FormHelperFactory } from '../../form/form-helper.factory';
import { industrySectorValidator } from '../../form/industry-sector.validator';
import { phoneNumberValidator } from '../../form/phone-number.validator';
import { Language } from '../../i18n/i18n';
import { OnboardingService } from '../../onboarding/onboarding.service';
import { routes } from '../../routes/routes';

interface Form {
  email: FormControl<string>;
  firstname: FormControl<string>;
  lastname: FormControl<string>;
  termsAccepted: FormControl<boolean>;
  company: FormControl<string>;
  companyType: FormControl<CompanyType>;
  industrySector: FormControl<IndustrySector>;
  phoneNumber: FormControl<string>;
}

@Component({
  selector: 'app-onboarding-brand-page',
  templateUrl: './onboarding-brand-page.component.html',
  styleUrls: ['./onboarding-brand-page.component.scss'],
})
export class OnboardingBrandPageComponent {
  form: FormGroup<Form>;
  formHelper: FormHelper<Form>;
  isSaving = false;

  protected readonly routes = routes;

  constructor(
    private onboardingService: OnboardingService,
    private translateService: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    formHelperFactory: FormHelperFactory,
  ) {
    const fb = new FormBuilder();
    this.form = fb.group(
      {
        email: fb.nonNullable.control('', [Validators.required, Validators.email]),
        firstname: fb.nonNullable.control('', [Validators.required]),
        lastname: fb.nonNullable.control('', [Validators.required]),
        termsAccepted: fb.nonNullable.control(false, [Validators.requiredTrue]),
        company: fb.nonNullable.control('', [Validators.required]),
        companyType: fb.nonNullable.control(CompanyType.Brand, [Validators.required, companyTypeValidator()]),
        industrySector: fb.nonNullable.control(IndustrySector.Ecommerce, [Validators.required, industrySectorValidator()]),
        phoneNumber: fb.nonNullable.control('', [Validators.required, phoneNumberValidator()]),
      },
      { updateOn: 'submit' },
    );
    this.formHelper = formHelperFactory.create(this.form);

    this.authService.currentUser$.pipe(take(1)).subscribe(user => {
      if (user) {
        this.form.controls.email.setValue(user.email ?? '');
      }
    });
  }

  get companyTypes() {
    return Object.values(CompanyType);
  }

  get industrySectors() {
    return Object.values(IndustrySector);
  }

  async register() {
    this.formHelper.trackSubmitted();

    if (!this.form.valid) {
      return;
    }

    const ref = this.activatedRoute.snapshot.queryParams['ref'];

    try {
      this.isSaving = true;
      await this.onboardingService.completeAsBrand({
        email: this.form.controls.email.value,
        firstname: this.form.controls.firstname.value,
        lastname: this.form.controls.lastname.value,
        company: this.form.controls.company.value,
        industrySector: this.form.controls.industrySector.value,
        companyType: this.form.controls.companyType.value,
        language: this.translateService.currentLang as Language,
        phoneNumber: this.form.controls.phoneNumber.value,
        onboardedVia: ref,
      });
    } finally {
      this.isSaving = false;
    }
  }
}
